import { useState, useEffect, useRef } from "react";
import { createFilterOptions, isValidId, updateActiveTask, Util } from '../../util/Util'
import RefreshButton from "../../components/RefreshButton";
import { useSale } from "./SaleContext";
import { useAuth } from "../../hooks/useAuth";
import { useAppSocket } from "../../providers/AppSocketContext";
import { MIN_DATE, MYSQL_LIMITS } from "../../util/constants";
import { useUI } from "../../providers/MoleUIContext";
import { Autocomplete, Grid, TextField } from "@mui/material";
import AddressLineButton from "../client/AddressLineButton";
import { detachFromBook } from "../client/contactBlock";
import AddressLineDialog from "../client/AddressLineDialog";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { businessTodayDate, parseISODay } from "../../util/businessClock";

interface Props {
	setValidateCheck: (value: boolean) => void;
}

const SaleDetail = ({ setValidateCheck }: Props) => {
	const [filteredClients, setFilteredClients] = useState<any[]>([]);
	const [agents, setAgents] = useState<any[]>([]);
	const [filteredAgents, setFilteredAgents] = useState<any[]>([]);
	const [isClientLoading, setIsClientLoading] = useState<boolean>(false);
	const [errorInput, setErrorInput] = useState("");

	const { setPendingTasks, activeTask, clients, setClients } = useSale();
	const [addressOpen, setAddressOpen] = useState(false);
	const [createdClientId, setCreatedClientId] = useState<number | null>(null);
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { api } = useAuth();



	const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;
	const allowAgents = Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_AGENTS?.value);

	const fetchClients = async (selectId?: number) => {
		setIsClientLoading(true);

		const res = await api.get("/clients",
			{
				params: {
					Active: true,
					Type: "CONSUMER"
				}
			}
		);

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
				setClients(res.data);
				setFilteredClients(res.data);
				// filter agents from clients
				const agentsData = res.data.filter((client: any) => client.is_agent);
				setAgents(agentsData);
				setFilteredAgents(agentsData);
				if (selectId && res.data.some((client: any) => client.id === selectId)) {
					updateActiveTask(setPendingTasks, { receiver: selectId });
				}
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchClients);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsClientLoading(false);
	};

	const onReceiverChanged = (receiver: number) => {
		// A saved address belongs to the client it was saved under, so switching
		// client keeps what was typed but drops the link to that entry.
		const changed = receiver !== activeTask?.receiver;
		updateActiveTask(setPendingTasks, {
			receiver,
			...(changed && activeTask?.contact ? { contact: detachFromBook(activeTask.contact) } : {}),
		});
	};

	const onAgentChanged = (agent: number) => {
		updateActiveTask(setPendingTasks, { agent });
	};

	const onRefChanged = (reference: string = Util.getInvoiceNumber()) => {
		updateActiveTask(setPendingTasks, { reference });
	};

	const onDateChanged = (taskDate: Date = businessTodayDate()) => {
		updateActiveTask(setPendingTasks, { date: Util.formatISODate(taskDate) });
	};

	// Hydrate agent/client filters from SaleContext cache; fetch only when empty (first open).
	// Layout mode switches remount this panel — do not re-fetch if clients already loaded.
	useEffect(() => {
		if (clients.length > 0) {
			const agentsData = clients.filter((client: any) => client.is_agent);
			setAgents(agentsData);
			return;
		}
		if (!isClientLoading) {
			void fetchClients();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clients]);

	useEffect(() => {
		if (!Util.isBoolSettingEnabled(appSettings?.ALLOW_SAME_AGENT_CLIENT?.value) && activeTask && activeTask.receiver > 0) {
			const filter = agents.filter((agent) => agent.id !== activeTask.receiver);
			setFilteredAgents(filter);
		} else {
			setFilteredAgents(agents);
		}
	}, [activeTask?.receiver]);

	useEffect(() => {
		if (!Util.isBoolSettingEnabled(appSettings?.ALLOW_SAME_AGENT_CLIENT?.value) && activeTask && (activeTask?.agent ?? -1) > 1) {
			const filter = clients.filter((client) => client.id !== activeTask.agent);
			setFilteredClients(filter);
		} else {
			setFilteredClients(clients);
		}
	}, [activeTask?.agent]);

	useEffect(() => {
		if (activeTask && activeTask.receiver > 0) {
			onReceiverChanged(activeTask.receiver);
		} else if (filteredClients.length > 0) {
			onReceiverChanged(filteredClients[0].id);
		}
	}, [filteredClients]);

	useEffect(() => {
		const isValid = isValidId(filteredClients, activeTask?.receiver) && (!activeTask?.agent || isValidId(filteredAgents, activeTask.agent));

		setValidateCheck(isValid);
	}, [activeTask, filteredClients, filteredAgents]);

	useEffect(() => {
		if (activeTask && (activeTask?.agent ?? -1) > 1) {
			onAgentChanged(activeTask.agent!);
		}
	}, [filteredAgents]);



	return (
		<Grid container spacing={0}>
			{allowAgents &&
				<Grid size={{ xs: 6, sm: 3, md: 3 }} >
					<Autocomplete
						id="agentInput"
						size="small"
						options={filteredAgents}
						filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
						getOptionLabel={(agent) => agent.acc_name || ""}
						value={filteredAgents.find((a) => a.id === activeTask?.agent) || null}
						onChange={(_, newValue) => {
							onAgentChanged(newValue?.id || null);
						}}
						loading={isClientLoading}
						autoComplete
						disabled={!activeTask || isClientLoading}
						renderInput={(params) => (
							<TextField
								{...params}
								label={
									<span>
										{t("common:ui.labels.agent")}&nbsp;&nbsp;
										<RefreshButton isWorking={isClientLoading} onClick={fetchClients} disabled={isClientLoading} />
									</span>
								}
								variant="filled"
								fullWidth
							/>
						)}
					/>
				</Grid>
			}
			<Grid size={{ xs: allowAgents ? 6 : 4, sm: allowAgents ? 3 : 4, md: allowAgents ? 3 : 4 }} >
				<Autocomplete
					id="clientInput"
					size="small"
					options={filteredClients}
					filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
					getOptionLabel={(client) => client.acc_name || ""}
					value={filteredClients.find((c) => c.id === activeTask?.receiver) || null}
					onChange={(_, newValue) => {
						onReceiverChanged(newValue?.id || -1);
					}}
					loading={isClientLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isClientLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:ui.labels.client")}&nbsp;&nbsp;
									<RefreshButton isWorking={isClientLoading} onClick={fetchClients} disabled={isClientLoading} />
									<AddressLineButton
										contact={activeTask?.contact}
										disabled={!activeTask}
										onClick={() => setAddressOpen(true)}
									/>
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: allowAgents ? 6 : 4, sm: allowAgents ? 3 : 4, md: allowAgents ? 3 : 4 }} >
				<TextField
					id="referenceInput"
					size="small"
					label={
						<span>
							{t("common:ui.labels.reference")}&nbsp;&nbsp;
							<RefreshButton isWorking={false} onClick={onRefChanged} />
						</span>
					}
					variant="filled"
					fullWidth
					type="text"
					value={activeTask?.reference || ""}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
					onBlur={(e) => {
						if (!e.target.value || e.target.value.trim() === "") {
							onRefChanged();
						}
					}}
					onChange={(e) => onRefChanged(e.target.value.trim())}
					disabled={!activeTask}
				/>
			</Grid>
			<Grid size={{ xs: allowAgents ? 6 : 4, sm: allowAgents ? 3 : 4, md: allowAgents ? 3 : 4 }} >
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={
							<span>
								{t("common:ui.labels.date")}&nbsp;&nbsp;
								<RefreshButton isWorking={false} onClick={onDateChanged} />
							</span>
						}
						value={activeTask?.date ? parseISODay(activeTask?.date) as Date : businessTodayDate()}
						minDate={(parseISODay(minDate) as Date)}
						onChange={(newDate) => {
							if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < (parseISODay(minDate) as Date)) {
								setErrorInput("dateInput");
								setTimeout(() => setErrorInput(""), 1500);
								return;
							}
							const date = newDate instanceof Date ? newDate : newDate.toDate(); // ✅ safe narrowing
							onDateChanged(date);
						}}
						disabled={!activeTask}
						slotProps={{
							textField: {
								id: "dateInput",
								size: "small",
								fullWidth: true,
								variant: "filled", // ✅ makes the textfield filled
								error: errorInput === "dateInput", // ✅ built-in error state
								helperText:
									errorInput === "dateInput" ? t("common:validation.invalidDate") : "", // ✅ error message
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<AddressLineDialog
				open={addressOpen}
				onClose={() => {
					setAddressOpen(false);
					// A client created from here is only in the local list; reload
					// it from the server now that the dialog is out of the way.
					if (createdClientId) {
						fetchClients(createdClientId);
						setCreatedClientId(null);
					}
				}}
				clientId={activeTask?.receiver}
				value={activeTask?.contact}
				existingNames={clients.map((client: any) => client.acc_name)}
				onApply={(contact) => updateActiveTask(setPendingTasks, { contact })}
				onClientCreated={(client) => {
					setClients((prev: any[]) => [...prev, client].sort((a, b) => String(a.acc_name).localeCompare(String(b.acc_name))));
					updateActiveTask(setPendingTasks, { receiver: client.id });
					setCreatedClientId(client.id);
				}}
			/>
		</Grid>
	);
};

export default SaleDetail;

